// src/components/chat/ChatConversation.js
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import pb from '../../lib/pb';
import {
  getPeer,
  generateSessionAESKey,
  wrapAESKey,
  unwrapAESKey,
  aesGcmEncrypt,
  aesGcmDecrypt,
  importRSAPublicKey,
  getRSAPrivateKey,
  getRSAPublicKey,
} from '../../lib/peer';
import { useAuth } from '../../contexts/AuthContext';
import { Avatar, injectStyles } from '../../shared/PostCard';
import { startCall } from '../CallUI';
import { sendFilesTo } from '../FileTransfer';
import { v4 as uuidv4 } from 'uuid';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import DoneRoundedIcon from '@mui/icons-material/DoneRounded';
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import CallRoundedIcon from '@mui/icons-material/CallRounded';
import VideocamRoundedIcon from '@mui/icons-material/VideocamRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import PhotoCameraRoundedIcon from '@mui/icons-material/PhotoCameraRounded';
import MicRoundedIcon from '@mui/icons-material/MicRounded';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded';

/* ================================================================== */
/* Shared helpers (ChatInbox.js imports these too)                     */
/* ================================================================== */

// never let the PocketBase SDK auto-cancel parallel chat requests
export const NC = { requestKey: null, $autoCancel: false };

export const makeChatId = (a, b) => [a, b].sort().join('_');

// PocketBase dates look like "2026-10-11 22:43:57.538Z" (a space, which Safari can't parse)
export const toTime = (v) => {
  if (!v) return 0;
  const t = new Date(String(v).replace(' ', 'T')).getTime();
  return Number.isNaN(t) ? 0 : t;
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/* ---------- sounds ---------- */
const sounds = {};
export function playSound(name) {
  try {
    if (!sounds[name]) sounds[name] = new Audio(`/sounds/${name}.mp3`);
    const a = sounds[name];
    a.currentTime = 0;
    const p = a.play();
    if (p && p.catch) p.catch(() => {});
  } catch {}
}

/* ---------- presence ---------- */
export function toMs(v) {
  if (v == null || v === '') return null;
  if (typeof v === 'number') {
    if (v > 1e12) return v;
    if (v > 1e9) return v * 1000;
    return null;
  }
  const t = Date.parse(String(v).replace(' ', 'T'));
  return Number.isNaN(t) ? null : t;
}

// NOTE: "peerId" is a fixed id ("u_<id>") and says nothing about being online.
export function isUserOnline(u) {
  if (!u) return false;
  if (u.isOnline === true) return true;
  const ms = toMs(u.lastSeen);
  return !!ms && Date.now() - ms < 120000;
}

function agoLabel(ms) {
  const s = Math.max(0, Math.floor((Date.now() - ms) / 1000));
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

const displayName = (u) => (u && ((u.name && u.name.trim()) || u.username)) || 'Chat';

/* ---------- friendly errors ---------- */
export function friendlyChatError(err, name = 'your friend') {
  const code = err && err.message;
  if (code === 'NO_PEER_KEY') {
    return `${name} hasn't finished setting up encryption yet. Ask them to open the app once while logged in.`;
  }
  if (code === 'KEYS_NOT_READY') {
    return "Your encryption keys aren't ready yet. Wait a moment, or log out and back in.";
  }
  if (code === 'UNWRAP_FAILED') {
    return "Your encryption keys don't match this chat's secure session. Log out and back in; if it keeps happening the chat key needs a reset.";
  }
  if (err && err.status === 403) return "You're not allowed to do that. Check the PocketBase collection rules.";
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return "You're offline.";
  return 'Something went wrong. Please try again.';
}

/* ================================================================== */
/* Secure session ("key ring")                                         */
/*   One permanent AES session per chat. The OLDEST session record     */
/*   wins, so both people always pick the same key, even if both       */
/*   created one at the same moment. Older sessions stay in the ring   */
/*   so history from before this update can still be opened.           */
/* ================================================================== */
const ringCache = new Map();
const ringJobs = new Map();

async function waitFor(getter, tries = 40, gap = 200) {
  for (let i = 0; i < tries; i += 1) {
    let v = null;
    try { v = await getter(); } catch {}
    if (v) return v;
    await sleep(gap);
  }
  throw new Error('KEYS_NOT_READY');
}

async function listSessions(chatId) {
  return pb.collection('chat_sessions').getFullList({
    filter: `chat_id="${chatId}"`,
    sort: 'created,id',
    ...NC,
  });
}

async function unwrapSession(session, myId) {
  const wrapped = session.sender === myId
    ? session.wrapped_key_for_sender
    : session.wrapped_key_for_receiver;
  if (!wrapped) return null;
  const priv = await waitFor(() => getRSAPrivateKey());
  return unwrapAESKey(wrapped, priv);
}

async function createSession(myId, otherId, chatId) {
  const other = await pb.collection('users').getOne(otherId, NC);
  if (!other || !other.rsa_public_key) throw new Error('NO_PEER_KEY');

  const targetPub = await importRSAPublicKey(other.rsa_public_key);
  const myPub = await waitFor(() => getRSAPublicKey());
  const aes = await generateSessionAESKey();
  const forTarget = await wrapAESKey(aes, targetPub);
  const forMe = await wrapAESKey(aes, myPub);

  return pb.collection('chat_sessions').create({
    chat_id: chatId,
    sender: myId,
    receiver: otherId,
    wrapped_key_for_sender: forMe,
    wrapped_key_for_receiver: forTarget,
    version: 1,
    expiresAt: new Date(Date.now() + 3650 * 86400000).toISOString(), // sessions no longer expire
  }, NC);
}

async function buildRing(myId, otherId, create) {
  const chatId = makeChatId(myId, otherId);
  let sessions = await listSessions(chatId);

  if (!sessions.length) {
    if (!create) return null;
    const rec = await createSession(myId, otherId, chatId);
    sessions = await listSessions(chatId);
    if (!sessions.length) sessions = [rec];
    else if (sessions[0].id !== rec.id) {
      // somebody else's session is older: theirs wins, drop mine
      pb.collection('chat_sessions').delete(rec.id, NC).catch(() => {});
      sessions = sessions.filter((s) => s.id !== rec.id);
    }
  }

  const keys = [];
  const ids = [];
  let primary = null;
  for (let i = 0; i < sessions.length; i += 1) {
    try {
      const k = await unwrapSession(sessions[i], myId);
      if (k) {
        keys.push(k);
        ids.push(sessions[i].id);
        if (i === 0) primary = k;
      }
    } catch (err) {
      if (err && err.message === 'KEYS_NOT_READY') throw err;
    }
  }
  if (!keys.length) throw new Error('UNWRAP_FAILED');
  return { chatId, keys, ids, primary };
}

export function resolveRing(myId, otherId, create = false, fresh = false) {
  const chatId = makeChatId(myId, otherId);
  const cacheKey = `${myId}|${chatId}`;
  if (!fresh) {
    const hit = ringCache.get(cacheKey);
    if (hit && (hit.primary || !create)) return Promise.resolve(hit);
  }
  const jobKey = `${cacheKey}:${create ? 1 : 0}`;
  if (ringJobs.has(jobKey)) return ringJobs.get(jobKey);

  const job = buildRing(myId, otherId, create)
    .then((r) => { if (r) ringCache.set(cacheKey, r); return r; })
    .finally(() => ringJobs.delete(jobKey));
  ringJobs.set(jobKey, job);
  return job;
}

export async function decryptWithRing(ring, ciphertext, iv) {
  let lastErr = null;
  for (const k of ring.keys) {
    try { return await aesGcmDecrypt(k, ciphertext, iv); } catch (e) { lastErr = e; }
  }
  throw lastErr || new Error('DECRYPT_FAILED');
}

/* ================================================================== */
/* Message helpers                                                     */
/* ================================================================== */
const PAGE = 40;
const GROUP_MS = 5 * 60 * 1000;
const PRIVACY_KEY = 'mf-chat-privacy';
const RANK = { sending: 0, failed: 0, sent: 1, delivered: 2, read: 3 };

function readPrivacy() {
  try { return localStorage.getItem(PRIVACY_KEY) === '1'; } catch { return false; }
}

function fromRecord(rec) {
  return {
    id: rec.id,
    messageId: rec.messageId || rec.id,
    from: rec.from,
    to: rec.to,
    ciphertext: rec.ciphertext,
    iv: rec.iv,
    created: rec.created,
    ts: toTime(rec.created),
    status: rec.status || 'sent',
    plain: null,
    failed: false,
    local: false,
    error: '',
  };
}

// a message never goes "back" from sent / delivered / read to sending / failed
function pickStatus(a, b) {
  const ra = RANK[a] ?? 1;
  const rb = RANK[b] ?? 1;
  if (ra >= 1 && rb < 1) return a;
  if (ra < 1 && rb >= 1) return b;
  if (ra < 1 && rb < 1) return b;
  return rb >= ra ? b : a;
}

function mergeMsg(old, m) {
  return {
    ...old,
    ...m,
    id: m.id || old.id,
    status: pickStatus(old.status, m.status),
    plain: m.plain != null ? m.plain : old.plain,
    failed: m.plain != null ? false : !!old.failed,
    ts: old.local ? old.ts : m.ts,
    local: !!old.local,
    error: '',
  };
}

function mergeInto(prev, incoming) {
  const map = new Map(prev.map((m) => [m.messageId, m]));
  for (const m of incoming) {
    const old = map.get(m.messageId);
    map.set(m.messageId, old ? mergeMsg(old, m) : m);
  }
  return Array.from(map.values()).sort(
    (a, b) => a.ts - b.ts || (a.messageId < b.messageId ? -1 : 1)
  );
}

const dayKey = (ts) => new Date(ts).toDateString();

function dayLabel(ts) {
  const d = new Date(ts);
  const today = new Date();
  const yest = new Date();
  yest.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yest.toDateString()) return 'Yesterday';
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: d.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
  });
}

const clockLabel = (ts) =>
  new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

function subscribeSafe(collection, topic, handler) {
  try {
    const p = pb.collection(collection).subscribe(topic, handler);
    if (p && p.catch) p.catch(() => {});
    return p;
  } catch {
    return null;
  }
}

function unsubSafe(u) {
  try {
    if (u && u.then) u.then((f) => f && f()).catch(() => {});
    else if (u) u();
  } catch {}
}

/* ================================================================== */
/* Styles (also used by ChatInbox.js)                                  */
/* ================================================================== */
const CONV_CSS = `
.mfc-iconbtn{flex:none;width:40px;height:40px;border-radius:12px;border:0;background:transparent;color:var(--mf-muted);display:grid;place-items:center;
  cursor:pointer;transition:background-color .15s,color .15s;text-decoration:none}
.mfc-iconbtn:hover{background:var(--mf-surface-2);color:var(--mf-text)}
.mfc-iconbtn.is-on{background:var(--mf-accent-soft);color:var(--mf-accent)}
.mfc-iconbtn:disabled{opacity:.45;cursor:not-allowed}
.mfc-iconbtn svg{font-size:22px}

.mfc-avwrap{position:relative;flex:none;display:inline-flex}
.mfc-dot{position:absolute;right:-1px;bottom:-1px;width:13px;height:13px;border-radius:50%;background:var(--mf-faint);border:2.5px solid var(--mf-surface)}
.mfc-dot.on{background:var(--mf-success)}

.mfc-conv{position:relative;display:flex;flex-direction:column;height:100%;min-height:0;background:var(--mf-bg);color:var(--mf-text)}

/* header */
.mfc-head{flex:none;display:flex;align-items:center;gap:6px;padding:10px 12px;background:var(--mf-surface);border-bottom:1px solid var(--mf-border)}
.mfc-head .mfc-avwrap{margin-left:4px;margin-right:4px}
.mfc-who{flex:1;min-width:0}
.mfc-name{font-weight:800;font-size:16px;line-height:1.25;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.mfc-sub{font-size:12.5px;line-height:1.3;color:var(--mf-faint);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.mfc-sub.is-online{color:var(--mf-success)}
.mfc-sub.is-typing{color:var(--mf-accent);font-weight:700}

/* banners */
.mfc-banner{flex:none;display:flex;align-items:center;gap:10px;padding:10px 14px;font-size:13.5px;line-height:1.4;background:rgba(255,107,107,.12);
  border-bottom:1px solid rgba(255,107,107,.35);color:#ff8a8a}
.mfc-banner span{flex:1;min-width:0}
.mf-feed[data-mf-theme="light"] .mfc-banner{color:#c0392b}
.mfc-banner.is-info{background:var(--mf-surface-2);border-bottom-color:var(--mf-border);color:var(--mf-muted)}
.mfc-banner button{flex:none;display:inline-flex;align-items:center;gap:6px;height:32px;padding:0 12px;border-radius:10px;border:1px solid currentColor;
  background:transparent;color:inherit;font:inherit;font-size:13px;font-weight:700;cursor:pointer}
.mfc-banner button svg{font-size:16px}

/* messages */
.mfc-body{position:relative;flex:1;min-height:0;display:flex;flex-direction:column}
.mfc-list{flex:1;min-height:0;overflow-y:auto;overscroll-behavior:contain;padding:14px 14px 8px;display:flex;flex-direction:column;-webkit-overflow-scrolling:touch}
.mfc-secure{align-self:center;display:inline-flex;align-items:center;gap:6px;margin:4px 0 8px;padding:6px 12px;border-radius:999px;background:var(--mf-surface-2);
  border:1px solid var(--mf-border);color:var(--mf-faint);font-size:12px;font-weight:600;text-align:center}
.mfc-conv .mfc-secure svg{font-size:14px}
.mfc-more{align-self:center;margin:2px 0 8px;height:34px;padding:0 16px;border-radius:999px;border:1px solid var(--mf-border-strong);background:var(--mf-surface);
  color:var(--mf-text);font:inherit;font-size:13px;font-weight:700;cursor:pointer}
.mfc-more:disabled{opacity:.6;cursor:default}
.mfc-day{align-self:center;margin:14px 0 6px;padding:4px 12px;border-radius:999px;background:var(--mf-surface-2);border:1px solid var(--mf-border);
  color:var(--mf-muted);font-size:12px;font-weight:700}

.mfc-row{display:flex;margin-top:2px;animation:mfc-in .18s ease-out both}
.mfc-row.is-first{margin-top:10px}
.mfc-row.mine{justify-content:flex-end}
.mfc-col{display:flex;flex-direction:column;max-width:min(80%,540px);min-width:0}
.mfc-row.mine .mfc-col{align-items:flex-end}
.mfc-row.theirs .mfc-col{align-items:flex-start}
.mfc-bubble{position:relative;max-width:100%;padding:8px 12px 6px;border-radius:18px;font-size:15px;line-height:1.45;word-break:break-word;overflow-wrap:anywhere;
  white-space:pre-wrap;box-shadow:0 1px 2px rgba(0,0,0,.16)}
.mfc-row.theirs .mfc-bubble{background:var(--mf-surface-2);color:var(--mf-text);border:1px solid var(--mf-border)}
.mfc-row.mine .mfc-bubble{background:var(--mf-grad);color:#fff}
.mfc-row.mine:not(.is-first) .mfc-bubble{border-top-right-radius:6px}
.mfc-row.mine:not(.is-last) .mfc-bubble{border-bottom-right-radius:6px}
.mfc-row.theirs:not(.is-first) .mfc-bubble{border-top-left-radius:6px}
.mfc-row.theirs:not(.is-last) .mfc-bubble{border-bottom-left-radius:6px}
.mfc-row.mine.is-last .mfc-bubble{border-bottom-right-radius:6px}
.mfc-row.theirs.is-last .mfc-bubble{border-bottom-left-radius:6px}
.mfc-bubble.is-tap{cursor:pointer}
.mfc-hidden{font-style:italic;opacity:.8}
.mfc-meta{display:flex;justify-content:flex-end;align-items:center;gap:4px;margin-top:2px;font-size:10.5px;line-height:1;opacity:.78}
.mfc-conv .mfc-tick{font-size:15px}
.mfc-conv .mfc-tick.is-read{color:#9be7ff;opacity:1}
.mfc-conv .mfc-tick.is-failed{color:#ffd0d0}
.mfc-retry{margin-top:4px;padding:2px 4px;border:0;background:transparent;color:#ff6b6b;font:inherit;font-size:12.5px;font-weight:700;cursor:pointer}
.mfc-retry:hover{text-decoration:underline}
@keyframes mfc-in{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}

.mfc-typing{align-self:flex-start;display:inline-flex;gap:4px;margin-top:10px;padding:12px 14px;border-radius:18px;border-bottom-left-radius:6px;
  background:var(--mf-surface-2);border:1px solid var(--mf-border)}
.mfc-typing i{width:7px;height:7px;border-radius:50%;background:var(--mf-muted);animation:mfc-bounce 1.2s infinite ease-in-out}
.mfc-typing i:nth-child(2){animation-delay:.15s}
.mfc-typing i:nth-child(3){animation-delay:.3s}
@keyframes mfc-bounce{0%,60%,100%{transform:translateY(0);opacity:.5}30%{transform:translateY(-4px);opacity:1}}

.mfc-empty{margin:auto;max-width:300px;text-align:center;color:var(--mf-muted);padding:24px 12px}
.mfc-empty-ico{width:72px;height:72px;margin:0 auto 14px;border-radius:24px;display:grid;place-items:center;background:var(--mf-accent-soft);color:var(--mf-accent);font-size:34px}
.mfc-empty h3{margin:0 0 6px;font-size:18px;color:var(--mf-text)}
.mfc-empty p{margin:0;font-size:14px;line-height:1.5}

.mfc-sk{height:40px;border-radius:18px;max-width:62%;background:linear-gradient(90deg,var(--mf-surface-2) 25%,var(--mf-surface-3) 37%,var(--mf-surface-2) 63%);
  background-size:400% 100%;animation:mfc-shimmer 1.4s ease infinite}
.mfc-sk.mine{align-self:flex-end}
@keyframes mfc-shimmer{0%{background-position:100% 50%}100%{background-position:0 50%}}

.mfc-jump{position:absolute;right:16px;bottom:14px;z-index:3;display:inline-flex;align-items:center;gap:6px;height:42px;padding:0 14px 0 10px;border-radius:999px;
  border:1px solid var(--mf-border-strong);background:var(--mf-surface);color:var(--mf-text);box-shadow:0 8px 24px rgba(0,0,0,.35);cursor:pointer;font:inherit;
  font-size:13px;font-weight:800}
.mfc-jump svg{font-size:22px}
.mfc-jump b{min-width:20px;height:20px;padding:0 6px;border-radius:999px;display:grid;place-items:center;background:var(--mf-grad);color:#fff;font-size:11px}

/* attachment tray (camera / voice / photos / files) */
.mfc-tray{flex:none;display:flex;justify-content:center;gap:22px;padding:12px 12px 10px;background:var(--mf-surface);border-top:1px solid var(--mf-border);
  animation:mfc-in .16s ease-out both}
.mfc-tray-btn{display:flex;flex-direction:column;align-items:center;gap:6px;border:0;background:transparent;color:var(--mf-muted);font:inherit;font-size:11.5px;
  font-weight:700;cursor:pointer}
.mfc-tray-btn span{width:52px;height:52px;border-radius:16px;display:grid;place-items:center;background:var(--mf-surface-2);border:1px solid var(--mf-border);
  color:var(--mf-text);transition:background-color .15s,transform .12s}
.mfc-tray-btn:hover span{background:var(--mf-surface-3)}
.mfc-tray-btn:active span{transform:scale(.94)}
.mfc-tray-btn svg{font-size:26px}
.mfc-tray-btn:focus-visible span{outline:2px solid var(--mf-accent);outline-offset:2px}

.mfc-rec{flex:none;display:flex;align-items:center;gap:10px;padding:10px 14px;background:var(--mf-surface);border-top:1px solid var(--mf-border);font-size:14px;font-weight:700}
.mfc-rec i{width:10px;height:10px;border-radius:50%;background:#ef4444;animation:mfc-blink 1s infinite}
.mfc-rec em{flex:1;font-style:normal;color:var(--mf-muted);font-variant-numeric:tabular-nums}
.mfc-rec button{height:34px;padding:0 14px;border-radius:10px;border:1px solid var(--mf-border-strong);background:var(--mf-surface-2);color:var(--mf-text);font:inherit;
  font-size:13px;font-weight:700;cursor:pointer}
.mfc-rec button.is-send{background:var(--mf-grad);border-color:transparent;color:#fff}
@keyframes mfc-blink{50%{opacity:.25}}

.mfc-toast{position:absolute;left:50%;bottom:84px;transform:translateX(-50%);z-index:5;max-width:calc(100% - 28px);padding:9px 14px;border-radius:12px;background:#111827;
  color:#fff;font-size:13px;font-weight:600;box-shadow:0 10px 30px rgba(0,0,0,.4);animation:mfc-in .18s ease-out both}

/* composer */
.mfc-comp{flex:none;display:flex;align-items:flex-end;gap:10px;padding:10px 12px calc(10px + env(safe-area-inset-bottom));background:var(--mf-surface);
  border-top:1px solid var(--mf-border)}
.mfc-plus{flex:none;width:46px;height:46px;border-radius:50%;border:0;background:transparent;color:var(--mf-muted);display:grid;place-items:center;cursor:pointer;
  transition:transform .2s,color .15s,background-color .15s}
.mfc-plus:hover{background:var(--mf-surface-2);color:var(--mf-text)}
.mfc-plus.is-open{transform:rotate(45deg);color:var(--mf-accent)}
.mfc-plus svg{font-size:28px}
.mfc-ta{flex:1;min-width:0;resize:none;max-height:140px;border-radius:23px;border:1px solid var(--mf-border-strong);background:var(--mf-surface-2);color:var(--mf-text);
  padding:12px 18px;font:inherit;font-size:16px;line-height:1.35;outline:none;transition:border-color .15s,box-shadow .15s}
.mfc-ta::placeholder{color:var(--mf-faint)}
.mfc-ta:focus{border-color:var(--mf-accent);box-shadow:0 0 0 3px var(--mf-accent-soft)}
.mfc-send{flex:none;width:46px;height:46px;border-radius:50%;border:0;background:var(--mf-grad);color:#fff;display:grid;place-items:center;cursor:pointer;
  box-shadow:0 6px 16px rgba(0,0,0,.3);transition:filter .15s,opacity .15s,transform .1s}
.mfc-send:hover{filter:brightness(1.08)}
.mfc-send:active{transform:scale(.94)}
.mfc-send:disabled{opacity:.4;cursor:default;box-shadow:none;filter:grayscale(.3)}
.mfc-send svg{font-size:22px}

/* locked */
.mfc-locked{margin:auto;max-width:340px;text-align:center;padding:24px;color:var(--mf-muted)}
.mfc-locked h3{margin:12px 0 6px;color:var(--mf-text);font-size:18px}
.mfc-locked p{margin:0;font-size:14px;line-height:1.5}
.mfc-locked .mfc-empty-ico{margin-bottom:0}

@media (prefers-reduced-motion:reduce){
  .mfc-row,.mfc-typing i,.mfc-sk,.mfc-rec i,.mfc-tray,.mfc-toast{animation:none!important}
}
`;
injectStyles('mfc-conv-css', CONV_CSS);

/* ================================================================== */
/* Small components                                                    */
/* ================================================================== */
function Ticks({ status }) {
  if (status === 'sending') return <AccessTimeRoundedIcon className="mfc-tick" />;
  if (status === 'failed') return <ErrorOutlineRoundedIcon className="mfc-tick is-failed" />;
  if (status === 'read') return <DoneAllRoundedIcon className="mfc-tick is-read" />;
  if (status === 'delivered') return <DoneAllRoundedIcon className="mfc-tick" />;
  return <DoneRoundedIcon className="mfc-tick" />;
}

const mmss = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/* ================================================================== */
/* Conversation                                                        */
/* ================================================================== */
export default function ChatConversation({ userId, friend, onBack }) {
  const toUserId = userId;
  const { user, peerReady } = useAuth();
  const me = user && user.id;

  const [target, setTarget] = useState(friend || null);
  const [unlocked, setUnlocked] = useState(null);       // null = still checking (shown optimistically)
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [reloadTick, setReloadTick] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [text, setText] = useState('');
  const [typing, setTyping] = useState(false);
  const [linked, setLinked] = useState(false);
  const [ring, setRing] = useState(null);
  const [keyError, setKeyError] = useState('');
  const [offline, setOffline] = useState(typeof navigator !== 'undefined' && navigator.onLine === false);
  const [privacy, setPrivacy] = useState(readPrivacy);
  const [revealed, setRevealed] = useState(null);
  const [showJump, setShowJump] = useState(false);
  const [unseen, setUnseen] = useState(0);

  // attachments, voice notes
  const [trayOpen, setTrayOpen] = useState(false);
  const [rec, setRec] = useState(null);                 // { start } while recording a voice note
  const [, setRecTick] = useState(0);
  const [notice, setNotice] = useState('');

  const listRef = useRef(null);
  const taRef = useRef(null);
  const ringRef = useRef(null);
  const messagesRef = useRef([]);
  const pageRef = useRef(1);
  const stickRef = useRef(true);
  const firstScroll = useRef(true);
  const preserveRef = useRef(null);
  const markedRef = useRef(new Set());
  const connsRef = useRef(new Set());
  const typingTimer = useRef(null);
  const lastTypingSent = useRef(0);
  const lastRingTry = useRef(0);
  const lastSync = useRef(0);
  const warmed = useRef(false);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const fileRef = useRef(null);
  const recorderRef = useRef(null);
  const recChunks = useRef([]);
  const recCancel = useRef(false);
  const noticeTimer = useRef(null);

  messagesRef.current = messages;

  const friendName = displayName(target || friend);

  const pairFilter = useMemo(
    () => `(from="${me}" && to="${toUserId}") || (from="${toUserId}" && to="${me}")`,
    [me, toUserId]
  );

  /* ---------- key ring ---------- */
  const applyRing = useCallback((r) => {
    const prevIds = ringRef.current ? ringRef.current.ids.join(',') : '';
    ringRef.current = r;
    setRing(r);
    if (r && prevIds !== r.ids.join(',')) {
      // keys changed: give undecryptable messages another try
      setMessages((prev) => prev.map((m) => (m.plain == null ? { ...m, failed: false } : m)));
    }
  }, []);

  const refreshRing = useCallback(async () => {
    if (!me) return;
    const now = Date.now();
    if (now - lastRingTry.current < 4000) return;
    lastRingTry.current = now;
    try {
      const r = await resolveRing(me, toUserId, false, true);
      if (r) applyRing(r);
      else setMessages((prev) => prev.map((m) => (m.plain == null && !m.failed ? { ...m, failed: true } : m)));
    } catch (err) {
      setKeyError(friendlyChatError(err, friendName));
      setMessages((prev) => prev.map((m) => (m.plain == null && !m.failed ? { ...m, failed: true } : m)));
    }
  }, [me, toUserId, applyRing, friendName]);

  /* ---------- first-time setup: lock check, friend, key ring ---------- */
  useEffect(() => {
    if (!me || !toUserId) return undefined;
    let cancelled = false;

    pb.collection('match_sessions')
      .getFirstListItem(
        `unlocked=true && ((owner="${me}" && target="${toUserId}") || (owner="${toUserId}" && target="${me}"))`,
        NC
      )
      .then(() => { if (!cancelled) setUnlocked(true); })
      .catch(() => { if (!cancelled) setUnlocked(false); });

    pb.collection('users')
      .getOne(toUserId, NC)
      .then((u) => { if (!cancelled) setTarget(u); })
      .catch(() => {});

    resolveRing(me, toUserId, false)
      .then((r) => { if (!cancelled && r) applyRing(r); })
      .catch((err) => { if (!cancelled) setKeyError(friendlyChatError(err, friendName)); });

    return () => { cancelled = true; };
  }, [me, toUserId, applyRing]);

  // make the very first message instant: create the session when the composer is focused
  const prewarm = useCallback(() => {
    if (warmed.current || !me) return;
    warmed.current = true;
    resolveRing(me, toUserId, true)
      .then((r) => { if (r) { applyRing(r); setKeyError(''); } })
      .catch((err) => setKeyError(friendlyChatError(err, friendName)));
  }, [me, toUserId, applyRing, friendName]);

  /* ---------- history ---------- */
  useEffect(() => {
    if (!me || !toUserId) return undefined;
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    (async () => {
      try {
        const res = await pb.collection('messages').getList(1, PAGE, { filter: pairFilter, sort: '-created', ...NC });
        if (cancelled) return;
        pageRef.current = 1;
        setMessages((prev) => mergeInto(prev, res.items.map(fromRecord)));
        setHasMore(res.page < res.totalPages);
      } catch {
        if (!cancelled) setLoadError('Could not load your messages.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [me, toUserId, pairFilter, reloadTick]);

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const next = pageRef.current + 1;
      const res = await pb.collection('messages').getList(next, PAGE, { filter: pairFilter, sort: '-created', ...NC });
      const el = listRef.current;
      if (el) preserveRef.current = { h: el.scrollHeight, t: el.scrollTop };
      pageRef.current = next;
      setMessages((prev) => mergeInto(prev, res.items.map(fromRecord)));
      setHasMore(res.page < res.totalPages);
    } catch {} finally {
      setLoadingMore(false);
    }
  };

  // pull anything we may have missed (tab was asleep, connection dropped, ...)
  const syncLatest = useCallback(async () => {
    if (!me) return;
    const now = Date.now();
    if (now - lastSync.current < 2500) return;
    lastSync.current = now;
    try {
      const res = await pb.collection('messages').getList(1, PAGE, { filter: pairFilter, sort: '-created', ...NC });
      setMessages((prev) => mergeInto(prev, res.items.map(fromRecord)));
    } catch {}
  }, [me, pairFilter]);

  /* ---------- realtime: messages ---------- */
  useEffect(() => {
    if (!me || !toUserId) return undefined;

    const sub = subscribeSafe('messages', '*', (e) => {
      const rec2 = e && e.record;
      if (!rec2) return;
      const mine = rec2.from === me && rec2.to === toUserId;
      const theirs = rec2.from === toUserId && rec2.to === me;
      if (!mine && !theirs) return;

      if (e.action === 'delete') {
        const mid = rec2.messageId || rec2.id;
        setMessages((prev) => prev.filter((m) => m.messageId !== mid));
        return;
      }

      setMessages((prev) => mergeInto(prev, [fromRecord(rec2)]));

      if (e.action === 'create' && theirs) {
        setTyping(false);
        playSound('receive');
        if (!stickRef.current) setUnseen((n) => n + 1);
      }
    });

    return () => unsubSafe(sub);
  }, [me, toUserId]);

  /* ---------- realtime: friend's presence ---------- */
  useEffect(() => {
    if (!toUserId) return undefined;
    const sub = subscribeSafe('users', toUserId, (e) => {
      if (e && e.record) setTarget((prev) => ({ ...(prev || {}), ...e.record }));
    });
    return () => unsubSafe(sub);
  }, [toUserId]);

  // keep "last seen" text fresh
  const [, forceTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => forceTick((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, []);

  /* ---------- typing indicator (best-effort peer-to-peer; messages never depend on it) ---------- */
  useEffect(() => {
    if (!peerReady || !me || !toUserId) return undefined;
    let peer = null;
    try { peer = getPeer(); } catch {}
    if (!peer) return undefined;

    const remoteId = (target && target.peerId) || `u_${toUserId}`;
    const conns = connsRef.current;
    let dead = false;

    const sync = () => { if (!dead) setLinked(Array.from(conns).some((c) => c.open)); };

    const attach = (c) => {
      if (conns.has(c)) return;
      c._mfAt = Date.now();
      conns.add(c);
      c.on('open', sync);
      c.on('data', (d) => {
        if (d && d.type === 'typing') {
          setTyping(true);
          clearTimeout(typingTimer.current);
          typingTimer.current = setTimeout(() => setTyping(false), 2800);
        }
      });
      const drop = () => { conns.delete(c); sync(); };
      c.on('close', drop);
      c.on('error', drop);
      sync();
    };

    const onIncoming = (c) => {
      if (c && c.peer === remoteId && c.label === 'chat') attach(c);
    };
    try { peer.on('connection', onIncoming); } catch {}

    const connect = () => {
      if (dead || peer.destroyed || peer.disconnected || !peer.open) return;
      conns.forEach((c) => {
        if (!c.open && Date.now() - (c._mfAt || 0) > 15000) {
          try { c.close(); } catch {}
          conns.delete(c);
        }
      });
      if (conns.size) return;
      try { attach(peer.connect(remoteId, { reliable: true, label: 'chat' })); } catch {}
    };
    connect();
    const timer = setInterval(connect, 6000);

    return () => {
      dead = true;
      clearInterval(timer);
      clearTimeout(typingTimer.current);
      try { peer.off('connection', onIncoming); } catch {}
      conns.forEach((c) => { try { c.close(); } catch {} });
      conns.clear();
      setLinked(false);
      setTyping(false);
    };
  }, [peerReady, me, toUserId, target && target.peerId]);

  const sendTyping = () => {
    const now = Date.now();
    if (now - lastTypingSent.current < 1200) return;
    lastTypingSent.current = now;
    connsRef.current.forEach((c) => {
      try { if (c.open) c.send({ type: 'typing' }); } catch {}
    });
  };

  /* ---------- decrypt whatever is waiting ---------- */
  useEffect(() => {
    if (!messages.some((m) => m.plain == null && !m.failed)) return undefined;

    if (!ring) {
      refreshRing();   // the other person may have just created the secure session
      return undefined;
    }

    let cancelled = false;
    const todo = messages.filter((m) => m.plain == null && !m.failed);
    (async () => {
      const done = new Map();
      await Promise.all(
        todo.map(async (m) => {
          try { done.set(m.messageId, { plain: await decryptWithRing(ring, m.ciphertext, m.iv), failed: false }); }
          catch { done.set(m.messageId, { plain: null, failed: true }); }
        })
      );
      if (cancelled) return;
      setMessages((prev) => prev.map((m) => (done.has(m.messageId) ? { ...m, ...done.get(m.messageId) } : m)));
      if (Array.from(done.values()).some((r) => r.failed)) refreshRing();
    })();

    return () => { cancelled = true; };
  }, [messages, ring, refreshRing]);

  // retry the secure session while something is still waiting for it
  useEffect(() => {
    const t = setInterval(() => {
      if (!ringRef.current && messagesRef.current.some((m) => m.plain == null)) refreshRing();
    }, 8000);
    return () => clearInterval(t);
  }, [refreshRing]);

  /* ---------- delivered / read receipts ---------- */
  const markIncoming = useCallback(() => {
    if (!me) return;
    const visible = document.visibilityState === 'visible';
    const focused = visible && (typeof document.hasFocus !== 'function' || document.hasFocus());
    const goal = focused ? 'read' : 'delivered';
    const goalRank = RANK[goal];

    const todo = messagesRef.current.filter(
      (m) =>
        m.from === toUserId &&
        m.to === me &&
        m.id &&
        (RANK[m.status] ?? 1) < goalRank &&
        !markedRef.current.has(`${m.id}:${goal}`)
    );
    if (!todo.length) return;

    const iso = new Date().toISOString();
    todo.forEach((m) => {
      const key = `${m.id}:${goal}`;
      markedRef.current.add(key);
      const data = goal === 'read' ? { status: 'read', readAt: iso } : { status: 'delivered', deliveredAt: iso };
      pb.collection('messages')
        .update(m.id, data, NC)
        .then(() => { if (goal === 'read') window.dispatchEvent(new Event('chat-read')); })
        .catch(() => markedRef.current.delete(key));
    });
  }, [me, toUserId]);

  useEffect(() => { markIncoming(); }, [messages, markIncoming]);

  /* ---------- come back to life after sleep / offline ---------- */
  useEffect(() => {
    const wake = () => { markIncoming(); syncLatest(); };
    const onVisible = () => { if (document.visibilityState === 'visible') wake(); };
    const goOnline = () => {
      setOffline(false);
      syncLatest();
      messagesRef.current
        .filter((m) => m.local && m.status === 'failed')
        .forEach((m) => deliverRef.current && deliverRef.current(m));
    };
    const goOffline = () => setOffline(true);

    window.addEventListener('focus', wake);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('focus', wake);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, [markIncoming, syncLatest]);

  /* ---------- sending ---------- */
  const deliverRef = useRef(null);

  const deliver = useCallback(async (m) => {
    const patch = (p) =>
      setMessages((prev) => prev.map((x) => (x.messageId === m.messageId ? { ...x, ...p } : x)));

    patch({ status: 'sending', error: '' });
    try {
      // a retry may find the first attempt actually went through
      if (m.status === 'failed') {
        const dup = await pb.collection('messages')
          .getFirstListItem(`messageId="${m.messageId}"`, NC)
          .catch(() => null);
        if (dup) {
          setMessages((prev) => mergeInto(prev, [{ ...fromRecord(dup), plain: m.plain }]));
          return;
        }
      }

      let { ciphertext, iv } = m;
      if (!ciphertext || !iv) {
        let r = ringRef.current;
        if (!r || !r.primary) {
          r = await resolveRing(me, toUserId, true);
          if (r) applyRing(r);
        }
        if (!r || !r.primary) throw new Error('UNWRAP_FAILED');
        const enc = await aesGcmEncrypt(r.primary, m.plain);
        ciphertext = enc.ciphertext;
        iv = enc.iv;
        patch({ ciphertext, iv });
      }

      const created = await pb.collection('messages').create(
        { messageId: m.messageId, from: me, to: toUserId, ciphertext, iv, status: 'sent' },
        NC
      );
      setMessages((prev) => mergeInto(prev, [{ ...fromRecord(created), plain: m.plain }]));
      playSound('send');
      setKeyError('');
    } catch (err) {
      const msg = friendlyChatError(err, friendName);
      patch({ status: 'failed', error: msg });
      if (err && ['NO_PEER_KEY', 'KEYS_NOT_READY', 'UNWRAP_FAILED'].includes(err.message)) setKeyError(msg);
    }
  }, [me, toUserId, applyRing, friendName]);

  deliverRef.current = deliver;

  const send = () => {
    const body = text.trim();
    if (!body || !me) return;
    setText('');
    const m = {
      id: '',
      messageId: uuidv4(),
      from: me,
      to: toUserId,
      ciphertext: '',
      iv: '',
      created: new Date().toISOString(),
      ts: Date.now(),
      status: 'sending',
      plain: body,
      failed: false,
      local: true,
      error: '',
    };
    stickRef.current = true;
    setMessages((prev) => mergeInto(prev, [m]));
    deliver(m);
    if (taRef.current) taRef.current.focus();
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !(e.nativeEvent && e.nativeEvent.isComposing)) {
      e.preventDefault();
      send();
    }
  };

  /* ---------- calls, files, voice notes ---------- */
  const flash = useCallback((t) => {
    setNotice(t);
    clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setNotice(''), 3200);
  }, []);

  // calls need no peer id: the friend is addressed by user id
  const placeCall = (type) => {
    if (!peerReady) { flash('Still connecting to the network. Try again in a moment.'); return; }
    startCall(toUserId, type, target || friend || { id: toUserId });
  };

  const handoff = useCallback((files) => {
    if (!files.length) return;
    sendFilesTo(toUserId, files, target || friend || { id: toUserId });
    flash(files.length === 1 ? `Sending “${files[0].name}”…` : `Sending ${files.length} files…`);
  }, [toUserId, target, friend, flash]);

  const pickFiles = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    setTrayOpen(false);
    handoff(files);
  };

  const startRec = async () => {
    if (rec) return;
    if (!navigator.mediaDevices || typeof window.MediaRecorder === 'undefined') {
      flash('Voice notes are not supported in this browser.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      const mime = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'].find((t) => window.MediaRecorder.isTypeSupported(t)) || '';
      const mr = new window.MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      recChunks.current = [];
      recCancel.current = false;
      mr.ondataavailable = (e) => { if (e.data && e.data.size) recChunks.current.push(e.data); };
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        if (recCancel.current || !recChunks.current.length) return;
        const type = mr.mimeType || 'audio/webm';
        const blob = new Blob(recChunks.current, { type });
        const ext = type.includes('mp4') ? 'm4a' : 'webm';
        handoff([new File([blob], `voice-note-${Date.now()}.${ext}`, { type })]);
      };
      mr.start();
      recorderRef.current = mr;
      setRec({ start: Date.now() });
      setTrayOpen(false);
    } catch {
      flash('Microphone not available. Check the browser permission.');
    }
  };

  const stopRec = (sendIt) => {
    recCancel.current = !sendIt;
    try { if (recorderRef.current && recorderRef.current.state !== 'inactive') recorderRef.current.stop(); } catch {}
    recorderRef.current = null;
    setRec(null);
  };

  useEffect(() => {
    if (!rec) return undefined;
    const t = setInterval(() => setRecTick((n) => n + 1), 500);
    return () => clearInterval(t);
  }, [rec]);

  // never leave the microphone on when the chat closes
  useEffect(() => () => {
    recCancel.current = true;
    try { if (recorderRef.current && recorderRef.current.state !== 'inactive') recorderRef.current.stop(); } catch {}
    clearTimeout(noticeTimer.current);
  }, []);

  /* ---------- scrolling ---------- */
  const onScroll = () => {
    const el = listRef.current;
    if (!el) return;
    const gap = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickRef.current = gap < 90;
    setShowJump(gap > 260);
    if (stickRef.current) setUnseen(0);
  };

  const jumpToEnd = () => {
    const el = listRef.current;
    if (!el) return;
    stickRef.current = true;
    setUnseen(0);
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  };

  useLayoutEffect(() => {
    const el = listRef.current;
    if (!el) return;

    if (preserveRef.current) {
      const { h, t } = preserveRef.current;
      preserveRef.current = null;
      el.scrollTop = el.scrollHeight - h + t;
      return;
    }

    if (firstScroll.current && messages.length) {
      el.scrollTop = el.scrollHeight;
      firstScroll.current = false;
      stickRef.current = true;
      return;
    }

    const last = messages[messages.length - 1];
    if (stickRef.current || (last && last.local && last.from === me)) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }
  }, [messages, me]);

  useLayoutEffect(() => {
    const el = listRef.current;
    if (el && typing && stickRef.current) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [typing]);

  // composer grows with the text
  useLayoutEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 140)}px`;
  }, [text]);

  const togglePrivacy = () => {
    setPrivacy((v) => {
      const next = !v;
      try { localStorage.setItem(PRIVACY_KEY, next ? '1' : '0'); } catch {}
      return next;
    });
    setRevealed(null);
  };

  /* ---------- derived ---------- */
  const rows = useMemo(() => {
    const out = [];
    let prev = null;
    let lastDay = '';
    messages.forEach((m, i) => {
      const day = dayKey(m.ts);
      if (day !== lastDay) {
        out.push({ k: `d-${day}-${i}`, day: dayLabel(m.ts) });
        lastDay = day;
        prev = null;
      }
      const next = messages[i + 1];
      const joinPrev = prev && prev.from === m.from && m.ts - prev.ts < GROUP_MS;
      const joinNext = next && next.from === m.from && next.ts - m.ts < GROUP_MS && dayKey(next.ts) === day;
      out.push({ k: m.messageId, m, first: !joinPrev, last: !joinNext });
      prev = m;
    });
    return out;
  }, [messages]);

  const lastId = messages.length ? messages[messages.length - 1].messageId : null;
  const online = isUserOnline(target) || linked;
  const seenMs = toMs(target && target.lastSeen);

  let subText = 'Offline';
  let subClass = '';
  if (typing) { subText = 'typing…'; subClass = ' is-typing'; }
  else if (online) { subText = 'Online'; subClass = ' is-online'; }
  else if (seenMs) subText = `Last seen ${agoLabel(seenMs)}`;

  /* ---------- locked ---------- */
  if (unlocked === false) {
    return (
      <div className="mfc-conv">
        <div className="mfc-head">
          {onBack && (
            <button type="button" className="mfc-iconbtn" onClick={onBack} aria-label="Back">
              <ArrowBackRoundedIcon />
            </button>
          )}
        </div>
        <div className="mfc-locked">
          <div className="mfc-empty-ico"><LockRoundedIcon fontSize="inherit" /></div>
          <h3>Chat locked</h3>
          <p>Complete all 10 matching tasks with {friendName} to unlock messaging.</p>
        </div>
      </div>
    );
  }

  /* ---------- UI ---------- */
  return (
    <div className="mfc-conv">
      <header className="mfc-head">
        {onBack && (
          <button type="button" className="mfc-iconbtn" onClick={onBack} aria-label="Back to chats">
            <ArrowBackRoundedIcon />
          </button>
        )}

        <span className="mfc-avwrap">
          <Avatar user={target || friend} size={42} />
          <i className={`mfc-dot${online ? ' on' : ''}`} />
        </span>

        <div className="mfc-who">
          <div className="mfc-name">{friendName}</div>
          <div className={`mfc-sub${subClass}`}>{subText}</div>
        </div>

        <button
          type="button"
          className="mfc-iconbtn"
          onClick={() => placeCall('audio')}
          aria-label={`Audio call ${friendName}`}
          title="Audio call"
        >
          <CallRoundedIcon />
        </button>
        <button
          type="button"
          className="mfc-iconbtn"
          onClick={() => placeCall('video')}
          aria-label={`Video call ${friendName}`}
          title="Video call"
        >
          <VideocamRoundedIcon />
        </button>

        <button
          type="button"
          className={`mfc-iconbtn${privacy ? ' is-on' : ''}`}
          onClick={togglePrivacy}
          aria-pressed={privacy}
          aria-label={privacy ? 'Privacy mode on' : 'Privacy mode off'}
          title={privacy ? 'Privacy mode: older messages are hidden (tap one to reveal)' : 'Privacy mode: hide older messages'}
        >
          {privacy ? <VisibilityOffRoundedIcon /> : <VisibilityRoundedIcon />}
        </button>
      </header>

      {keyError && (
        <div className="mfc-banner" role="alert">
          <span>{keyError}</span>
          <button type="button" onClick={() => { setKeyError(''); lastRingTry.current = 0; warmed.current = false; refreshRing(); }}>
            <RefreshRoundedIcon /> Retry
          </button>
        </div>
      )}

      {offline && !keyError && (
        <div className="mfc-banner is-info" role="status">
          <span>You're offline. Messages will send when you're back.</span>
        </div>
      )}

      <div className="mfc-body">
        <div className="mfc-list" ref={listRef} onScroll={onScroll}>
          {hasMore && (
            <button type="button" className="mfc-more" onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? 'Loading…' : 'Load earlier messages'}
            </button>
          )}

          {!loading && !loadError && (
            <div className="mfc-secure">
              <LockRoundedIcon /> Messages are end-to-end encrypted
            </div>
          )}

          {loading && (
            <>
              <div className="mfc-sk" style={{ width: '55%' }} />
              <div className="mfc-sk mine" style={{ width: '40%', marginTop: 10 }} />
              <div className="mfc-sk" style={{ width: '62%', marginTop: 10 }} />
              <div className="mfc-sk mine" style={{ width: '48%', marginTop: 10 }} />
            </>
          )}

          {!loading && loadError && (
            <div className="mfc-empty">
              <div className="mfc-empty-ico">⚠️</div>
              <h3>Couldn't load messages</h3>
              <p>{loadError}</p>
              <button type="button" className="mfc-more" style={{ marginTop: 14 }} onClick={() => setReloadTick((n) => n + 1)}>
                Try again
              </button>
            </div>
          )}

          {!loading && !loadError && messages.length === 0 && (
            <div className="mfc-empty">
              <div className="mfc-empty-ico">👋</div>
              <h3>Say hi to {friendName}</h3>
              <p>Your first message sets up a private, encrypted channel between you two.</p>
            </div>
          )}

          {rows.map((r) => {
            if (r.day) return <div key={r.k} className="mfc-day">{r.day}</div>;

            const { m } = r;
            const mine = m.from === me;
            const hide = privacy && m.messageId !== lastId && revealed !== m.messageId && m.plain != null;

            let body;
            if (m.plain != null) {
              body = hide ? <span className="mfc-hidden">🔒 Encrypted message · tap to reveal</span> : m.plain;
            } else if (m.failed) {
              body = <span className="mfc-hidden">🔒 Can't decrypt this message</span>;
            } else {
              body = <span className="mfc-hidden">Decrypting…</span>;
            }

            return (
              <div
                key={r.k}
                className={`mfc-row ${mine ? 'mine' : 'theirs'}${r.first ? ' is-first' : ''}${r.last ? ' is-last' : ''}`}
              >
                <div className="mfc-col">
                  <div
                    className={`mfc-bubble${hide ? ' is-tap' : ''}`}
                    onClick={hide ? () => setRevealed(m.messageId) : undefined}
                  >
                    {body}
                    <div className="mfc-meta">
                      <span>{clockLabel(m.ts)}</span>
                      {mine && <Ticks status={m.status} />}
                    </div>
                  </div>

                  {mine && m.status === 'failed' && (
                    <button type="button" className="mfc-retry" onClick={() => deliver(m)} title={m.error || ''}>
                      Not sent · Tap to retry
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {typing && (
            <div className="mfc-typing" aria-label={`${friendName} is typing`}>
              <i /><i /><i />
            </div>
          )}
        </div>

        {showJump && (
          <button type="button" className="mfc-jump" onClick={jumpToEnd} aria-label="Jump to latest message">
            <KeyboardArrowDownRoundedIcon />
            {unseen > 0 ? <b>{unseen > 99 ? '99+' : unseen}</b> : 'Latest'}
          </button>
        )}
      </div>

      {notice && <div className="mfc-toast" role="status">{notice}</div>}

      {/* attachment tray: camera · voice note · photos & videos · files */}
      {trayOpen && !rec && (
        <div className="mfc-tray" role="toolbar" aria-label="Attach">
          <button type="button" className="mfc-tray-btn" onClick={() => cameraRef.current && cameraRef.current.click()}>
            <span><PhotoCameraRoundedIcon /></span>Camera
          </button>
          <button type="button" className="mfc-tray-btn" onClick={startRec}>
            <span><MicRoundedIcon /></span>Voice
          </button>
          <button type="button" className="mfc-tray-btn" onClick={() => galleryRef.current && galleryRef.current.click()}>
            <span><ImageRoundedIcon /></span>Photos
          </button>
          <button type="button" className="mfc-tray-btn" onClick={() => fileRef.current && fileRef.current.click()}>
            <span><InsertDriveFileRoundedIcon /></span>File
          </button>
        </div>
      )}

      {rec && (
        <div className="mfc-rec" role="status">
          <i aria-hidden="true" />
          <em>Recording… {mmss(Date.now() - rec.start)}</em>
          <button type="button" onClick={() => stopRec(false)}>Cancel</button>
          <button type="button" className="is-send" onClick={() => stopRec(true)}>Send</button>
        </div>
      )}

      <input ref={cameraRef} type="file" accept="image/*,video/*" capture="environment" hidden onChange={pickFiles} />
      <input ref={galleryRef} type="file" accept="image/*,video/*" multiple hidden onChange={pickFiles} />
      <input ref={fileRef} type="file" multiple hidden onChange={pickFiles} />

      <form className="mfc-comp" onSubmit={(e) => { e.preventDefault(); send(); }}>
        <button
          type="button"
          className={`mfc-plus${trayOpen ? ' is-open' : ''}`}
          onClick={() => setTrayOpen((o) => !o)}
          aria-expanded={trayOpen}
          aria-label={trayOpen ? 'Close attachments' : 'Attach files'}
          title="Attach"
        >
          <AddRoundedIcon />
        </button>
        <textarea
          ref={taRef}
          className="mfc-ta"
          rows={1}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            if (e.target.value.trim()) { sendTyping(); prewarm(); }
          }}
          onFocus={prewarm}
          onKeyDown={onKeyDown}
          placeholder={`Message ${friendName}…`}
          aria-label="Type a message"
          maxLength={4000}
          enterKeyHint="send"
        />
        <button type="submit" className="mfc-send" disabled={!text.trim()} aria-label="Send message">
          <SendRoundedIcon />
        </button>
      </form>
    </div>
  );
}